import React, { useState, useEffect } from 'react';
import { X, Eye, Package, ShoppingCart, CreditCard, CheckCircle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import api from '../../../api/axios';

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' });
}

/** Summarise tool input for display. */
function summariseInput(input) {
  if (!input) return null;
  let obj = input;
  if (typeof input === 'string') {
    try { obj = JSON.parse(input); } catch { return input; }
  }
  if (typeof obj !== 'object') return String(obj);
  const parts = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined && v !== '') {
      parts.push(`${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
    }
  }
  return parts.join(', ') || null;
}

// Map tool names to visitor activity categories (ecommerce funnel)
const activityCategories = [
  {
    id: 'page-view',
    label: 'Page View',
    icon: Eye,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    toolNames: ['get_store_info'],
  },
  {
    id: 'product-view',
    label: 'Product View',
    icon: Package,
    iconColor: 'text-violet-500',
    iconBg: 'bg-violet-50',
    toolNames: ['search_products', 'list_products', 'get_product_detail', 'get_best_sellers', 'search_products_by_price', 'get_product_kb_detail'],
  },
  {
    id: 'add-to-cart',
    label: 'Add to Cart',
    icon: ShoppingCart,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
    toolNames: ['recommend_products'],
  },
  {
    id: 'initiate-checkout',
    label: 'Initiate Checkout',
    icon: CreditCard,
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-50',
    toolNames: ['get_active_discounts', 'get_payment_info'],
  },
  {
    id: 'purchase',
    label: 'Purchase',
    icon: CheckCircle,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-50',
    toolNames: ['lookup_order', 'lookup_order_by_phone', 'get_order_detail', 'get_delivery_tracking', 'get_expected_delivery', 'get_refund_status', 'add_order_tags'],
  },
];

/** Group tool calls into visitor activity categories. */
function groupByCategory(toolCalls) {
  return activityCategories.map((cat) => {
    const events = toolCalls.filter((tc) => cat.toolNames.includes(tc.toolName));
    return { ...cat, events };
  });
}

const SAVisitorPanel = ({ sessionId, onClose }) => {
  const [toolCalls, setToolCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ 'product-view': true, 'add-to-cart': true });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/api/v1/shopping-assistance/sessions/${sessionId}/tools`)
      .then((res) => {
        if (!cancelled) setToolCalls(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setToolCalls([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const groups = groupByCategory(toolCalls);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-800">Visitor Activity</p>
            <p className="text-xs text-gray-400 mt-0.5">Session timeline</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          )}

          {!loading && groups.map((group) => {
            const Icon = group.icon;
            const isOpen = !!expanded[group.id];
            return (
              <div key={group.id} className="rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggle(group.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg ${group.iconBg} flex items-center justify-center`}>
                      <Icon size={14} className={group.iconColor} />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{group.label}</span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                      {group.events.length}
                    </span>
                  </div>
                  {isOpen ? (
                    <ChevronDown size={14} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={14} className="text-gray-400" />
                  )}
                </button>

                {isOpen && group.events.length > 0 && (
                  <div className="border-t border-gray-50 divide-y divide-gray-50">
                    {group.events.map((tc) => {
                      const inputSummary = summariseInput(tc.toolInput);
                      return (
                        <div key={tc.id} className="px-4 py-2.5 bg-gray-50/50">
                          <p className="text-xs text-blue-600 font-mono truncate" title={inputSummary}>
                            {inputSummary || tc.toolName}
                          </p>
                          {tc.toolName && (
                            <p className="text-xs text-gray-700 font-medium mt-0.5">
                              {tc.toolName.replace(/_/g, ' ')}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">{formatTime(tc.startedAt)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {isOpen && group.events.length === 0 && (
                  <div className="border-t border-gray-50 px-4 py-3 bg-gray-50/50">
                    <p className="text-xs text-gray-400 italic">No activity recorded</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SAVisitorPanel;
